/**
 * CR-6440: Dependent-filter cascade logic for the Purchasing → Forecast filter panel.
 *
 * Implements the same cascade behaviour as PO Headers (usePOHeaderFilterCascades):
 *   - Division → Site (filter sites by selected division)
 *   - Company Country → Site (filter sites by selected country)
 *   - Site → Supplier (filter suppliers by selected site/customer)
 *   - Supplier Country → Supplier (filter suppliers by selected country)
 *   - Supplier Group → Supplier (filter suppliers by selected group)
 *   - Commodity Group → Commodity Type (filter types by selected group)
 *
 * The customer view panel uses these cascades to ensure filter dependencies
 * work correctly and maintain consistency with the PO Headers tab.
 */
import { useMemo } from 'react';

export interface ForecastCascadeFilterOptions {
  suppliers: Array<{
    id: number;
    name: string;
    group?: number | null;
    customers?: number[];  // Site IDs this supplier serves
    country?: string | null;  // Supplier's country for filtering
  }>;
  commodityGroups: string[];
  commodityTypes: Array<{ name: string; commodity_group: string }>;
  supplierGroups: Array<{ id: number; name: string }>;
  sites: Array<{
    id: number;
    name: string;
    group?: number | null;  // Division ID
    country?: string | null;
  }>;
  divisions: Array<{ id: number; name: string }>;
  siteCountries: string[];
  supplierCountries: string[];
}

export interface ForecastFilters {
  partNumber: string;
  poNumber: string;
  supplierPartNumber: string;
  partSearch: string;
  commodityGroup: string;
  commodityType: string;
  supplierGroup: string;
  supplierId: string;
  customerId: string;
  recordType: string;
  importDateFrom: string;
  importDateTo: string;
  dateFrom: string;
  dateTo: string;
  // CR-6440: New cascade filter fields
  division: string;
  companyCountry: string;
  site: string;
  supplierCountry: string;
}

export function useForecastFilterCascades(
  filters: ForecastFilters,
  filterOptions: ForecastCascadeFilterOptions | null,
  onFilterChange: (key: string, value: string) => void,
) {
  const suppliers = filterOptions?.suppliers ?? [];
  const commodityTypes = filterOptions?.commodityTypes ?? [];
  const sites = filterOptions?.sites ?? [];

  // Site options: narrowed by Division and/or Company Country.
  const siteOptions = useMemo(() => {
    let filtered = sites;

    // Filter by division if selected
    if (filters.division) {
      filtered = filtered.filter(
        (s) => String(s.group ?? '') === filters.division,
      );
    }

    // Filter by company country if selected
    if (filters.companyCountry) {
      filtered = filtered.filter(
        (s) => s.country === filters.companyCountry,
      );
    }

    // Keep currently selected site in the list even if it doesn't match
    if (filters.site && !filtered.some((s) => String(s.id) === filters.site)) {
      const selected = sites.find((s) => String(s.id) === filters.site);
      if (selected) return [selected, ...filtered];
    }
    return filtered;
  }, [sites, filters.division, filters.companyCountry, filters.site]);

  // Supplier options: narrowed by Site, Supplier Country, and/or Supplier Group.
  const supplierSiteOptions = useMemo(() => {
    let filtered = suppliers;

    // Filter by site if selected
    if (filters.site) {
      const siteId = parseInt(filters.site, 10);
      filtered = filtered.filter(
        (s) => s.customers?.includes(siteId) ?? false,
      );
    }

    // Filter by supplier country if selected
    if (filters.supplierCountry) {
      filtered = filtered.filter(
        (s) => s.country === filters.supplierCountry,
      );
    }

    // Filter by supplier group if selected
    if (filters.supplierGroup) {
      const groupName = filters.supplierGroup;
      const groupId = filterOptions?.supplierGroups.find(g => g.name === groupName)?.id;
      if (groupId) {
        filtered = filtered.filter(
          (s) => s.group === groupId,
        );
      }
    }

    // Keep currently selected supplier in the list even if it doesn't match
    if (filters.supplierId && !filtered.some((s) => String(s.id) === filters.supplierId)) {
      const selected = suppliers.find((s) => String(s.id) === filters.supplierId);
      if (selected) return [selected, ...filtered];
    }
    return filtered;
  }, [suppliers, filters.site, filters.supplierCountry, filters.supplierGroup, filters.supplierId, filterOptions?.supplierGroups]);

  // Commodity Type: narrowed by the selected Commodity Group.
  const commodityTypeOptions = useMemo(() => {
    if (!filters.commodityGroup) {
      return commodityTypes;
    }
    return commodityTypes.filter(
      (t) => t.commodity_group === filters.commodityGroup,
    );
  }, [commodityTypes, filters.commodityGroup]);

  // --- Cascade change handlers (clear now-invalid dependents) -------------

  /**
   * When Division changes, clear Site if it doesn't belong to the new division.
   */
  const handleDivisionChange = (value: string) => {
    onFilterChange('division', value);
    if (value && filters.site) {
      const selected = sites.find((s) => String(s.id) === filters.site);
      if (selected && String(selected.group ?? '') !== value) {
        onFilterChange('site', '');
        // Also clear supplier since site was cleared
        onFilterChange('supplierId', '');
      }
    }
  };

  /**
   * When Company Country changes, clear Site if it doesn't match the new country.
   */
  const handleCompanyCountryChange = (value: string) => {
    onFilterChange('companyCountry', value);
    if (value && filters.site) {
      const selected = sites.find((s) => String(s.id) === filters.site);
      if (selected && selected.country !== value) {
        onFilterChange('site', '');
        // Also clear supplier since site was cleared
        onFilterChange('supplierId', '');
      }
    }
  };

  /**
   * When Site changes, clear Supplier if it doesn't serve the new site.
   */
  const handleSiteChange = (value: string) => {
    onFilterChange('site', value);
    if (value && filters.supplierId) {
      const siteId = parseInt(value, 10);
      const selected = suppliers.find((s) => String(s.id) === filters.supplierId);
      if (selected && !(selected.customers?.includes(siteId) ?? false)) {
        onFilterChange('supplierId', '');
      }
    }
  };

  /**
   * When Supplier Country changes, clear Supplier if it doesn't match the new country.
   */
  const handleSupplierCountryChange = (value: string) => {
    onFilterChange('supplierCountry', value);
    if (value && filters.supplierId) {
      const selected = suppliers.find((s) => String(s.id) === filters.supplierId);
      if (selected && selected.country !== value) {
        onFilterChange('supplierId', '');
      }
    }
  };

  /**
   * When Supplier Group changes, clear Supplier if it doesn't belong to the new group.
   */
  const handleSupplierGroupChange = (value: string) => {
    onFilterChange('supplierGroup', value);
    if (value && filters.supplierId) {
      const groupId = filterOptions?.supplierGroups.find(g => g.name === value)?.id;
      const selected = suppliers.find((s) => String(s.id) === filters.supplierId);
      if (selected && groupId && selected.group !== groupId) {
        onFilterChange('supplierId', '');
      }
    }
  };

  /**
   * When Commodity Group changes, clear Commodity Type if it doesn't belong to the new group.
   */
  const handleCommodityGroupChange = (value: string) => {
    onFilterChange('commodityGroup', value);
    if (value && filters.commodityType) {
      const selected = commodityTypes.find(
        (t) => t.name === filters.commodityType,
      );
      if (selected && selected.commodity_group !== value) {
        onFilterChange('commodityType', '');
      }
    }
  };

  return {
    siteOptions,
    supplierSiteOptions,
    commodityTypeOptions,
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierCountryChange,
    handleSupplierGroupChange,
    handleCommodityGroupChange,
  };
}
